import { API_BASE_URL } from '@/common/config/runtime.ts';
import {
  clearSseMessages,
  publishSseMessage,
  setSseLeader,
  setSseState,
  type SseEvent,
} from '@/common/notifications/cross-tab-sse-store.ts';
import { parseJsonOrString } from '@/common/notifications/utils.ts';

let eventSource: EventSource | null = null;
let releaseLeaderLock: (() => void) | null = null;
let lockRequestActive = false;
let shouldRun = false;
let broadcastChannel: BroadcastChannel | null = null;

const SSE_CHANNEL_NAME = 'pac-sse';

export function startNotificationsSse() {
  if (typeof window === 'undefined') return;

  shouldRun = true;
  getBroadcastChannel();

  if (!('locks' in navigator)) {
    setSseState('connecting');
    openEventSource();
    return;
  }

  if (lockRequestActive) return;

  lockRequestActive = true;

  setSseState('listening');

  void navigator.locks
    .request('pac-sse-leader', { mode: 'exclusive' }, async () => {
      if (!shouldRun) return;

      setSseLeader(true);
      openEventSource();

      await new Promise<void>((resolve) => {
        releaseLeaderLock = resolve;
      });

      releaseLeaderLock = null;
      setSseLeader(false);
      closeEventSource();
    })
    .catch((err) => {
      console.error('SSE leader lock failed', err);
      setSseState('error');
    })
    .finally(() => {
      lockRequestActive = false;

      if (shouldRun && !eventSource) {
        startNotificationsSse();
      }
    });
}

export function stopNotificationsSse() {
  shouldRun = false;
  releaseLeaderLock?.();
  releaseLeaderLock = null;
  setSseLeader(false);
  closeEventSource();
  closeBroadcastChannel();
  clearSseMessages();
}

function openEventSource() {
  if (eventSource) return;

  const source = new EventSource(`${API_BASE_URL}/api/v1/notifications/sse`, {
    withCredentials: true,
  });

  eventSource = source;

  setSseState('connected');

  source.onerror = (err) => {
    console.error('SSE error', err, source.readyState);
    setSseState('error');
  };

  source.onmessage = (event) => {
    publishMessage('message', event);
  };

  source.addEventListener('ping', (event) => {
    publishMessage('ping', event);
  });

  source.addEventListener('notifications.snapshot', (event) => {
    publishMessage('notifications.snapshot', event);
  });

  source.addEventListener('notification.in_app.created', (event) => {
    publishMessage('notification.in_app.created', event);
  });
}

function closeEventSource() {
  setSseState('idle');

  eventSource?.close();
  eventSource = null;
}

function publishMessage(eventName: string, event: Event) {
  if (!(event instanceof MessageEvent)) return;

  const message = {
    id: event.lastEventId || createMessageId(),
    event: eventName,
    data: parseJsonOrString(event.data),
    receivedAt: new Date().toISOString(),
  };

  publishSseMessage(message);
  getBroadcastChannel()?.postMessage(message);
}

function getBroadcastChannel() {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (broadcastChannel) return broadcastChannel;

  broadcastChannel = new BroadcastChannel(SSE_CHANNEL_NAME);
  setSseState(eventSource ? 'connected' : 'listening');
  broadcastChannel.onmessage = (event: MessageEvent<unknown>) => {
    if (isSseMessage(event.data)) {
      publishSseMessage(event.data);
    }
  };

  return broadcastChannel;
}

function closeBroadcastChannel() {
  broadcastChannel?.close();
  broadcastChannel = null;
}

function isSseMessage(value: unknown): value is SseEvent {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<SseEvent>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.event === 'string' &&
    'data' in candidate &&
    typeof candidate.receivedAt === 'string'
  );
}

function createMessageId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}:${Math.random().toString(36).slice(2)}`;
}
